/* eslint-disable react/no-this-in-sfc */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, positionName, pitchType, ObjectiveMetricChart, dateTranslate } from 'yankees-react-utils';
import styled from 'styled-components';
import moment from 'moment';
import MetricTable from './MetricTable';
import { useOktaQuery, PlayerDevObjectives } from '../../data';
import formatByLabelFormat from './formatByLabelFormat';

const MetricHeader = styled.div`
    font-weight: bold;
    font-size: 1rem;
    margin-bottom: 0.5rem;
`;

const ObjectiveMetric = ({ metric }) => {
    const { t: translate } = useTranslation();
    const hideLevelAvgs = metric?.Metric?.HideLevelAvgs || false;
    const { data: metricData, isFetching } = useOktaQuery(
        ['objectiveMetric', metric.ObjectiveId, metric.ObjectiveMetricID],
        (t) => PlayerDevObjectives.getObjectiveMetric(t, metric.ObjectiveID, metric.ObjectiveMetricID),
        {
            staleTime: 24 * 60 * 60 * 1000, // 1 day
        },
    );

    if (isFetching) {
        return (
            <div className="d-flex justify-content-center py-5">
                <LoadingSpinner />
            </div>
        );
    }

    const seriesify = (dataset, leagueavg) => {
        let chartEndDate;
        const series = [];

        if (dataset.playerdata?.length > 0) { chartEndDate = new Date(dataset.playerdata[dataset.playerdata.length - 1].gamedate); }

        if (!chartEndDate || chartEndDate.getMonth() < 8) { chartEndDate = new Date((new Date()).getFullYear(), 7, 31); }

        if (metric.Metric.ShowGameVals) {
            series.push({
                name: translate('sections.my-objectives.games'),
                type: 'scatter',
                color: '#ccccff',
                data: dataset.playerdata?.filter((t) => t.gameval != null).map((t) => ({
                    x: (new Date(t.gamedate)).getTime(),
                    y: t.gameval,
                })),
                zIndex: 100,
                stickyTracking: false,
                tooltip: {
                    headerFormat: '',
                    pointFormatter() {
                        if (this.name === 'avg' && leagueavg) { // must be plotline
                            return `${leagueavg.abbr.trim()} ${translate('common.abbr-average')}: ${formatByLabelFormat(metric.Metric.LabelFormat, leagueavg.lvavgrate)}`;
                        } if (this.name === 'target') {
                            return `${translate('sections.my-objectives.target')}: ${formatByLabelFormat(metric.Metric.LabelFormat, metric.TargetVal)}`;
                        }
                        return `${translate('common.date')}: ${dateTranslate(translate, moment.utc(this.x))}<br />${metric.Metric.StatName}: 
                            ${formatByLabelFormat(metric.Metric.LabelFormat, this.y)}`;
                    },
                },
            });
        }

        if (!hideLevelAvgs) {
            series.push({
                name: translate('sections.my-objectives.30-game-avg'),
                type: 'spline',
                dataLabels: {
                    enabled: false,
                },
                color: '#336699',
                data: dataset.playerdata.filter((t) => t.ytdavg != null).map((t) => ({
                    x: (new Date(t.gamedate)).getTime(),
                    y: t.ytdavg,
                })),
                zIndex: 200,
                stickyTracking: false,
                tooltip: {
                    headerFormat: '',
                    pointFormatter() {
                        return dateTranslate(translate, moment.utc(this.x));
                    },
                },
            });
        }

        return series;
    };

    const levels = metricData.levelchanges?.map((l) => {
        const gameDate = new Date(l.gamedate);
        return {
            levelAbbr: l.level_abbr,
            gameDate: new Date(`${gameDate.getFullYear()}/${gameDate.getMonth() + 1}/${gameDate.getDate()}`),
        };
    });

    const series = seriesify(metricData, metricData.leagueavg[0]);
    const minX = metricData.playerdata.length > 0 ? (new Date(metricData.playerdata[0].gamedate)) : null;
    const maxX = metricData.playerdata.length > 0 ? (new Date(metricData.playerdata[metricData.playerdata.length - 1].gamedate)) : null;
    const minY = metric?.Metric?.minY ? metric.Metric.minY : metricData.leagueavg.length > 0 ? Math.min(metricData.leagueavg[0].lvavgrate, metric.TargetVal, ...metricData.playerdata.map((x) => x.gameval)) : null;
    const maxY = metric?.Metric?.maxY ? metric.Metric.maxY : metricData.leagueavg.length > 0 ? Math.max(metricData.leagueavg[0].lvavgrate, metric.TargetVal, ...metricData.playerdata.map((x) => x.gameval)) : null;

    return (
        <div>
            <MetricHeader>
                { metric.Metric.StatName }
                { metric.Position && <span>&nbsp;| {translate('common.as')} {positionName(metric.Position, translate)}</span> }
                { metric.Split && <span>&nbsp;| {metric.Metric.StatType === 'p' ? metric.Split.PDescription : metric.Split.BDescription}</span> }
                { metric.Split2 && <span>&nbsp;| {metric.Metric.StatType === 'p' ? metric.Split2.PDescription : metric.Split2.BDescription}</span> }
                { metric.PitchType && <span>&nbsp;| {translate('common.on')} {pitchType(metric.PitchType, undefined, undefined, translate)}</span> }
                { metric.TargetVal && <span>&nbsp;| {translate('sections.my-objectives.target')}: {formatByLabelFormat(metric.Metric.LabelFormat, metric.TargetVal)}</span> }
            </MetricHeader>
            { metricData.playerdata.find((x) => x.gameval || x.gameval === 0)
                ? (
                    <div className="d-flex align-items-start">
                        <div className="flex-grow-1 m-0">
                            <ObjectiveMetricChart
                                minX={minX}
                                maxX={maxX}
                                minY={minY}
                                maxY={maxY}
                                levels={levels}
                                seriesData={series}
                                labelFormat={metric.Metric.LabelFormat}
                                leagueAvg={metricData.leagueavg && metricData.leagueavg.length > 0 ? metricData.leagueavg[0].lvavgrate : null}
                                targetVal={metric.TargetVal}
                                showLeagueAvgLineName={metricData.leagueavg && metricData.leagueavg.length > 0 ? `${metricData.leagueavg[0].abbr} ${translate('common.abbr-average')}` : null}
                                showTargetLineName={translate('sections.my-objectives.target')}
                                disableLegendInteractivity
                            />
                        </div>
                        <div className="ms-1">
                            {!hideLevelAvgs && <MetricTable metric={metric} metricData={metricData} />}
                        </div>
                    </div>
                )
                : <div>{translate('sections.my-objectives.no-game-data')}</div> }
        </div>
    );
};

export default ObjectiveMetric;
